/**
 * Export / import ความคืบหน้า
 *
 * ยังไม่มี cloud sync — ย้ายเครื่องด้วยไฟล์ JSON ไปก่อน
 * ทุก record มี updatedAt แล้ว ต่อ cloud sync ทีหลังได้โดยไม่ต้อง migrate
 */
import {
  getDrillAll,
  getLogSince,
  getOverrideAll,
  putDrill,
  putOverride,
  putProgressBulk,
  db,
} from './db'
import type { DrillStat, Progress, ReviewLog } from './types'
import type { CardOverride } from './db'

export interface Backup {
  format: 'jpt-backup'
  version: 1
  exportedAt: number
  progress: Progress[]
  drill: DrillStat[]
  overrides: CardOverride[]
  /** ประวัติย้อนหลัง 1 ปี — พอสำหรับ optimize FSRS */
  log: ReviewLog[]
}

const YEAR = 365 * 86_400_000

export async function exportBackup(): Promise<Backup> {
  const d = await db()
  const [progress, drill, overrides, log] = await Promise.all([
    d.getAll('progress'),
    getDrillAll(),
    getOverrideAll(),
    getLogSince(Date.now() - YEAR),
  ])
  return {
    format: 'jpt-backup',
    version: 1,
    exportedAt: Date.now(),
    progress,
    drill,
    overrides,
    log,
  }
}

export async function downloadBackup(): Promise<void> {
  const data = await exportBackup()
  const stamp = new Date(data.exportedAt).toISOString().slice(0, 10)
  const blob = new Blob([JSON.stringify(data)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `jpt-backup-${stamp}.json`
  a.click()
  URL.revokeObjectURL(url)
}

export function isBackup(v: unknown): v is Backup {
  if (typeof v !== 'object' || v === null) return false
  const b = v as Partial<Backup>
  return b.format === 'jpt-backup' && b.version === 1 && Array.isArray(b.progress)
}

/**
 * รวมข้อมูลเข้ากับที่มีอยู่ — ชนกันเอาอันที่ updatedAt ใหม่กว่า
 * ไม่ล้างของเดิมทิ้ง เพราะอาจ import ไฟล์เก่ากว่าโดยไม่ตั้งใจ
 */
export async function importBackup(data: Backup): Promise<{ progress: number; drill: number }> {
  const d = await db()

  const existing = new Map((await d.getAll('progress')).map((p) => [p.cardId, p]))
  const incoming = data.progress.filter((p) => {
    const cur = existing.get(p.cardId)
    return !cur || p.updatedAt > cur.updatedAt
  })
  await putProgressBulk(incoming)

  let drillCount = 0
  for (const s of data.drill ?? []) {
    const cur = await d.get('drill', s.cardId)
    if (!cur || s.updatedAt > cur.updatedAt) {
      await putDrill(s)
      drillCount++
    }
  }

  for (const o of data.overrides ?? []) {
    const cur = await d.get('overrides', o.cardId)
    if (!cur || o.updatedAt > cur.updatedAt) await putOverride(o)
  }

  return { progress: incoming.length, drill: drillCount }
}

export async function readBackupFile(file: File): Promise<Backup> {
  const text = await file.text()
  const parsed: unknown = JSON.parse(text)
  if (!isBackup(parsed)) throw new Error('ไฟล์นี้ไม่ใช่ backup ของ JPT')
  return parsed
}
